/** Processing, Missing - what was expected and has not arrived, from the source census (ADR-0212).
 *
 * The census compares what a workspace **expects** with what **arrived**, because a total is a sum
 * over what arrived and a sum over what arrived is indistinguishable from a complete inventory.
 * This tab is that comparison as a table: one row per expected-source window that is not accounted
 * for, in the census's own six states, **none of them merged** (`STATE`).
 *
 * - A window that has not closed is *pending*, never *missing*; an on-event source with nothing in
 *   it is *unknown*, because nothing arriving may mean nothing was serviced.
 * - A **declared nil** is a person's statement with a reason, and it is shown, with the way to take
 *   it back. Both acts are an Approver's or Admin's (ADR-0008): declaring that nothing happened is
 *   an assertion a filing rests on, not data entry, so a Contributor is shown the row and no button.
 * - The default view hides what is settled (`submitted` and `not_expected`) so the table is the
 *   work; the State filter reaches both, and the tiles count the server's own windows.
 *
 * A row opens the source (`/sources/{id}`) with its connections. The owner is shown as the server
 * says this reader may be told (ADR-0218): a name for the company's own roles, the role for an
 * Auditor, and *no owner* for a source nobody answers for. Never the word *assigned*, which names
 * nobody, and never an identifier.
 */

import { CheckCircle, Receipt } from "@phosphor-icons/react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { useMe } from "../../api/auth";
import { useCoverage, useDeclareNil, useWithdrawNil } from "../../api/sources";
import type { SourceCoverage, SourceWindow } from "../../api/sources";
import { ReadFailed, WriteFailed } from "../../components/Loaded";
import {
  Drawer,
  EmptyAction,
  EmptyState,
  EMPTY_FILTERS,
  FilterLine,
  GridTable,
  InfoPopover,
  RowMenu,
  StatusChip,
  Tile,
  TileStrip,
} from "../../components/ui";
import type { ChipTone, FilterDef, FilterState, GridColumn } from "../../components/ui";
import { periodShort, shortDate } from "../../lib/format";
import { RequirePeriod } from "../NoPeriod";
import { IngestionPanel } from "../IngestionPanel";
import { A_COLLEAGUE, useNameOf } from "../PersonRef";
import { keepSearch } from "../search";
import { STATE, nameOf } from "../sourceModel";
import { MAY_UPLOAD } from "../upload/useSendFiles";
import { DeclareExpected, SourceLinksPanel } from "../Sources";
import { ProcessingFrame, count } from "./common";
import styles from "./Processing.module.css";

const TONE: Record<string, ChipTone> = {
  submitted: "ok",
  declared_nil: "neutral",
  missing: "crit",
  pending: "info",
  unknown: "warn",
  not_expected: "neutral",
};

/** What the default view shows: every state that is still somebody's work. */
const OPEN_STATES = ["missing", "pending", "unknown", "declared_nil"];

interface Gap {
  key: string;
  source: SourceCoverage;
  window: SourceWindow;
}

function MissingBody({ from, to }: { from: string; to: string }) {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const coverage = useCoverage(from, to);
  const declareNil = useDeclareNil(from, to);
  const withdrawNil = useWithdrawNil(from, to);
  const mayDeclare = me?.role === "approver" || me?.role === "admin";
  const personName = useNameOf();
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [declaring, setDeclaring] = useState<Gap | null>(null);
  const [withdrawing, setWithdrawing] = useState<Gap | null>(null);
  const [adding, setAdding] = useState(false);
  const [trail, setTrail] = useState<Gap | null>(null);
  const [reason, setReason] = useState("");
  const [refusal, setRefusal] = useState<unknown>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const data = coverage.data;
  const gaps: Gap[] = (data?.sources ?? []).flatMap((source) =>
    source.windows.map((window) => ({ key: `${source.source_id}:${window.period_start}`, source, window })),
  );
  const states = filters.values.state ?? OPEN_STATES;
  const sitePick = filters.values.site?.[0];
  const sourcePick = filters.values.source?.[0];
  const needle = filters.search.trim().toLowerCase();
  const shown = gaps.filter(
    (gap) =>
      states.includes(gap.window.state) &&
      (!sitePick || gap.source.facility_id === sitePick) &&
      (!sourcePick || gap.source.source_id === sourcePick) &&
      (!needle || nameOf(gap.source).toLowerCase().includes(needle)),
  );

  const sources = data?.sources ?? [];
  const siteOptions = [...new Map(sources.map((s) => [s.facility_id, s.facility_name])).entries()].map(
    ([value, label]) => ({ value, label }),
  );
  const defs: FilterDef[] = [
    {
      id: "state",
      label: "State",
      options: Object.entries(STATE).map(([value, entry]) => ({
        value,
        label: entry.short.charAt(0).toUpperCase() + entry.short.slice(1),
      })),
    },
    { id: "site", label: "Site", mode: "single", allLabel: "Any site", options: siteOptions },
    {
      id: "source",
      label: "Source",
      mode: "single",
      allLabel: "Any source",
      options: sources.map((source) => ({ value: source.source_id, label: nameOf(source) })),
    },
  ];

  const ownerOf = (source: SourceCoverage): string => {
    if (!source.owner_user_id) return "No owner";
    return personName(source.owner_user_id) ?? A_COLLEAGUE;
  };

  const columns: GridColumn<Gap>[] = [
    {
      id: "source",
      header: "Source",
      minWidth: "200px",
      value: ({ source }) => nameOf(source),
      cell: ({ source }) => (
        <Link className={styles.link} to="/sources/$sourceId" params={{ sourceId: source.source_id }} search={keepSearch}>
          {nameOf(source)}
        </Link>
      ),
    },
    { id: "site", header: "Site", width: "164px", cell: ({ source }) => source.facility_name },
    {
      id: "period",
      header: "Period",
      width: "124px",
      value: ({ window }) => `${shortDate(window.period_start)} – ${shortDate(window.period_end)}`,
      cell: ({ window }) => periodShort(window.period_start, window.period_end),
    },
    {
      id: "state",
      header: "State",
      width: "128px",
      cell: ({ window }) => (
        <span data-testid="gap-state" data-state={window.state} title={STATE[window.state]?.long}>
          <StatusChip tone={TONE[window.state] ?? "neutral"} size="sm">
            {STATE[window.state]?.short ?? window.state}
          </StatusChip>
        </span>
      ),
    },
    { id: "owner", header: "Owner", width: "104px", cell: ({ source }) => ownerOf(source) },
    {
      // One line: "Show what arrived" is the row's action and the declare acts are in its menu. Two outlined
      // buttons stacked made every row 83px and clipped at 1024px (U6, 2026-11-03).
      id: "action",
      header: "Action",
      width: "208px",
      hideable: false,
      cell: (gap) => (
        <RowMenu
          name={nameOf(gap.source)}
          /* EXP-06: the count says what arrived; this says which, and links to each. */
          main={{ label: "Show what arrived", onSelect: () => setTrail(gap) }}
          items={[
            ...(mayDeclare && (gap.window.state === "missing" || gap.window.state === "unknown")
              ? [{ label: "Confirm none", onSelect: () => setDeclaring(gap) }]
              : []),
            ...(mayDeclare && gap.window.state === "declared_nil" && gap.window.nil_declaration_id
              ? [{ label: "Withdraw confirmation", onSelect: () => setWithdrawing(gap) }]
              : []),
          ]}
        />
      ),
    },
  ];

  const completeness = data?.completeness;
  // No source is expected at all: "0 missing" would read as every bill having arrived, so each tile is a
  // dash until something is expected (a count of things may be zero only when zero is true).
  const nothingExpected = Boolean(data) && gaps.length === 0;
  const tileCount = (value: number | undefined): string => (nothingExpected ? "-" : count(value));
  const closeForms = () => {
    setDeclaring(null);
    setWithdrawing(null);
    setReason("");
    setRefusal(null);
  };

  async function onDeclare() {
    if (!declaring) return;
    setRefusal(null);
    try {
      await declareNil.mutateAsync({
        sourceId: declaring.source.source_id,
        period_start: declaring.window.period_start,
        period_end: declaring.window.period_end,
        reason,
      });
      closeForms();
    } catch (error) {
      setRefusal(error);
    }
  }

  async function onWithdraw() {
    if (!withdrawing?.window.nil_declaration_id) return;
    setRefusal(null);
    try {
      await withdrawNil.mutateAsync({
        sourceId: withdrawing.source.source_id,
        declarationId: withdrawing.window.nil_declaration_id,
        reason,
      });
      closeForms();
    } catch (error) {
      setRefusal(error);
    }
  }

  const form = declaring ?? withdrawing;

  return (
    <>
    <ProcessingFrame
      tab="Missing"
      // COL-04: a census of what arrived says nothing about a feed that has stopped arriving, so a
      // stopped feed is said above the table. Renders nothing when no feed exists.
      extra={<IngestionPanel />}
      tiles={
        <TileStrip label="Expected bills">
          <Tile
            label="Missing bills"
            value={tileCount(completeness?.missing)}
            loading={coverage.isPending}
            testId="tile-missing"
            infoKey="processing.missing"
          />
          <Tile label="Confirmed none" value={tileCount(completeness?.declared_nil)} loading={coverage.isPending}
            testId="tile-nil" />
          <Tile label="Pending" value={tileCount(completeness?.pending)} loading={coverage.isPending}
            testId="tile-pending" />
          <Tile label="Unknown" value={tileCount(completeness?.unknown)} loading={coverage.isPending}
            testId="tile-unknown" />
          {/* COM-02: a reconciled census and a named owner fail independently, so they are counted
              apart; an unnamed owner is a staffing decision and not a collection problem. */}
          <Tile
            label="No owner"
            value={tileCount(completeness?.unowned)}
            loading={coverage.isPending}
            testId="tile-unowned"
            infoKey="processing.no-owner"
          />
        </TileStrip>
      }
      filters={
        gaps.length === 0 && !coverage.isPending && !coverage.error ? undefined : (
        <FilterLine
          filters={defs}
          state={filters}
          onChange={setFilters}
          searchLabel="Search expected sources"
          resultCount={data ? { shown: shown.length, total: gaps.length, noun: "bills" } : undefined}
          actions={
            <>
              {saved ? (
                <span data-tone="ok" role="status" data-testid="declared-expected">
                  Expected source added
                </span>
              ) : null}
              {/* The census screen: every expected source with its owner and life, the coverage grid
                  and who bills what. It is part of this tab (the strip keeps Missing lit). */}
              <Link
                className={styles.secondary}
                to="/processing"
                search={(prev: Record<string, unknown>) => ({ ...keepSearch(prev), tab: "sources" })}
                data-testid="open-census"
              >
                Expected bills
              </Link>
              {mayDeclare ? (
                <button className={styles.secondary} type="button" onClick={() => setAdding(true)}>
                  Declare an expected source
                </button>
              ) : null}
            </>
          }
        />
        )
      }
      table={
        coverage.error ? (
          <ReadFailed of="The expected-bills check" error={coverage.error} onRetry={() => void coverage.refetch()} />
        ) : (
          <>
            <GridTable
              caption="Expected bills and what arrived"
              testId="missing"
              columns={columns}
              rows={shown}
              rowKey={(gap) => gap.key}
              rowLabel={(gap) => `${nameOf(gap.source)}, ${gap.window.period_start}`}
              onRowOpen={(gap) =>
                void navigate({
                  to: "/sources/$sourceId",
                  params: { sourceId: gap.source.source_id },
                  search: keepSearch,
                })
              }
              loading={coverage.isPending}
              empty={
                <EmptyState
                  compact
                  testId="missing-empty"
                  icon={gaps.length === 0 ? Receipt : completeness?.is_complete ? CheckCircle : undefined}
                  title={
                    gaps.length === 0
                      ? "No source is expected yet"
                      : completeness?.is_complete
                        ? "Every expected bill has arrived"
                        : "Nothing matches"
                  }
                  action={
                    gaps.length === 0 ? (
                      <EmptyAction
                        label="Declare an expected source"
                        allowed={mayDeclare}
                        reason="An Admin or Approver declares expected sources."
                        onClick={() => setAdding(true)}
                        testId="missing-empty-declare"
                      />
                    ) : undefined
                  }
                >
                  {gaps.length === 0 ? "Say which bills you expect, and EmitIQ tells you which are missing." : null}
                </EmptyState>
              }
            />
            {gaps.length > 0 ? <InfoPopover infoKey="processing.missing" /> : null}
          </>
        )
      }
    />
    <Drawer
      open={form !== null}
      onClose={closeForms}
      title={declaring ? "Confirm none" : "Withdraw confirmation"}
      width="sm"
      footer={
        <button
          className={styles.primary}
          type="button"
          disabled={reason.trim() === "" || declareNil.isPending || withdrawNil.isPending}
          onClick={() => void (declaring ? onDeclare() : onWithdraw())}
        >
          {declaring ? "Confirm none" : "Withdraw confirmation"}
        </button>
      }
    >
      {form ? (
        <label className={styles.formField}>
          <span>
            {declaring ? "Why was there no activity?" : "Why is this declaration being taken back?"}
          </span>
          <textarea
            aria-label="Reason"
            value={reason}
            rows={4}
            onChange={(event) => setReason(event.target.value)}
          />
        </label>
      ) : null}
      <WriteFailed error={refusal} of="this declaration" onReload={() => setRefusal(null)} />
    </Drawer>
    <Drawer
      open={trail !== null}
      onClose={() => setTrail(null)}
      title={trail ? `What arrived: ${nameOf(trail.source)}` : "What arrived"}
      width="md"
    >
      {trail ? (
        <>
          {/* **The window that is missing, not the whole year** (Q-07, G-Wcontrib-05): a missing
              January listed April to December and said nothing about January. */}
          <SourceLinksPanel
            supply={trail.source.supply}
            sourceId={trail.source.source_id}
            from={trail.window.period_start}
            to={trail.window.period_end}
            meId={me?.user_id}
          />
          {me && MAY_UPLOAD.includes(me.role) && trail.window.state === "missing" ? (
            <Link className={styles.link} to="/upload" search={keepSearch} data-testid="gap-upload">
              Upload the bill
            </Link>
          ) : null}
        </>
      ) : null}
    </Drawer>
    <Drawer open={adding} onClose={() => setAdding(false)} title="Declare an expected source" width="md">
      <DeclareExpected
        from={from}
        to={to}
        openSignal={1}
        onDone={(message) => {
          if (message) {
            setSaved(message);
            setAdding(false);
          }
        }}
      />
    </Drawer>
    </>
  );
}

export function Missing() {
  return (
    <RequirePeriod
      title="Processing"
      crumb="Missing"
      tiles={["Missing bills", "Confirmed none", "Pending", "Unknown", "No owner"]}
    >
      {(_screen, current) => <MissingBody from={current.period_start} to={current.period_end} />}
    </RequirePeriod>
  );
}
